// ============================================================================
// STRING LIST EDITOR
// Reusable "input + Add button + removable chip list" editor for string[]
// fields. Extracted from the keyword-editing pattern duplicated across
// AvatarBuilder, QuickCreateAvatarModal, and ImagesTab's BulkTagModal.
// ============================================================================

import React, { useState } from 'react';
import { X } from 'lucide-react';

interface StringListEditorProps {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  hint?: string;
  addButtonLabel?: string;
}

export default function StringListEditor({
  label,
  items,
  onChange,
  placeholder = 'Enter a value and press Enter...',
  hint,
  addButtonLabel = 'Add'
}: StringListEditorProps) {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !items.includes(trimmed)) {
      onChange([...items, trimmed]);
      setInputValue('');
    }
  };

  const handleRemove = (item: string) => {
    onChange(items.filter(i => i !== item));
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-text-main mb-2">{label}</label>
      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
          placeholder={placeholder}
          className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-slate-400"
        />
        <button
          onClick={handleAdd}
          type="button"
          className="px-5 py-3 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy-dark transition-colors"
        >
          {addButtonLabel}
        </button>
      </div>
      <div className="flex gap-2 flex-wrap">
        {items.map((item) => (
          <span
            key={item}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/20 text-text-main rounded-lg text-xs font-semibold"
          >
            {item}
            <button
              onClick={() => handleRemove(item)}
              type="button"
              className="text-text-main/60 hover:text-text-main transition-colors"
            >
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      {hint && <p className="text-xs text-text-sub mt-1.5">{hint}</p>}
    </div>
  );
}
